import type { HTMLAttributes, ReactNode, TdHTMLAttributes, ThHTMLAttributes } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { PAGE_SIZE, type SortState } from '@/lib/hooks';
import { formatNumber } from '@/lib/format';
import { IconButton } from './button';

/** A data table that scrolls sideways on a narrow screen instead of squeezing its columns. */
export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('scroll-thin overflow-x-auto rounded-2xl border border-line bg-surface shadow-card', className)}>
      <table className="w-full min-w-[720px] border-collapse text-sm">{children}</table>
    </div>
  );
}

export function Th({ className, ...rest }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      className={cn('sticky top-0 z-[1] border-b border-line bg-surface px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-muted', className)}
      {...rest}
    />
  );
}

/** A header that sorts on click and shows which way. */
export function SortTh({ label, sortKey, sort, onSort, className, align }: {
  label: string;
  sortKey: string;
  sort: SortState;
  onSort: (key: string) => void;
  className?: string;
  align?: 'right';
}) {
  const active = sort.key === sortKey;
  const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <Th className={cn('p-0', className)} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn('flex h-full w-full items-center gap-1 px-4 py-2.5 text-left uppercase tracking-wider hover:text-ink', align === 'right' && 'justify-end', active && 'text-ink')}
      >
        {label}
        <Icon size={12} className={cn(active ? 'text-accent' : 'text-faint opacity-60')} />
      </button>
    </Th>
  );
}

export function Td({ className, ...rest }: TdHTMLAttributes<HTMLTableCellElement>) {
  return <td className={cn('border-b border-line px-4 py-3 align-middle', className)} {...rest} />;
}

export function Tr({ className, onClick, ...rest }: HTMLAttributes<HTMLTableRowElement>) {
  return (
    <tr
      onClick={onClick}
      className={cn('transition-colors [&:last-child>td]:border-b-0 hover:bg-surface-2/70', onClick && 'cursor-pointer', className)}
      {...rest}
    />
  );
}

/** "1–25 из 340" with arrows; hidden when everything fits on one page. */
export function Pagination({ page, pages, total, onPage }: { page: number; pages: number; total: number; onPage: (p: number) => void }) {
  if (pages <= 1) return null;
  const from = (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(total, page * PAGE_SIZE);
  return (
    <div className="flex items-center justify-between px-1 pt-3 text-xs text-muted">
      <span>{formatNumber(from)}–{formatNumber(to)} из {formatNumber(total)}</span>
      <div className="flex items-center gap-1">
        <IconButton size="sm" title="Предыдущая страница" disabled={page <= 1} onClick={() => onPage(page - 1)}><ChevronLeft size={15} /></IconButton>
        <span className="min-w-[4rem] text-center tabular-nums">{page} / {pages}</span>
        <IconButton size="sm" title="Следующая страница" disabled={page >= pages} onClick={() => onPage(page + 1)}><ChevronRight size={15} /></IconButton>
      </div>
    </div>
  );
}
